import { defaultRules } from "@envstash/core-wasm";
import type { Rule } from "@envstash/protocol";
import { createFileRoute } from "@tanstack/react-router";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { Confirm } from "@/components/confirm";
import { EmptyState, ErrorText, PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/icon-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { errorMessage } from "@/lib/api";
import { useVault } from "@/lib/hooks";
import { vaultStore } from "@/lib/vault-store";

export const Route = createFileRoute("/_app/rules")({ component: RulesPage });

const MODES = [
  { value: "sync", label: "sync" },
  { value: "backup", label: "backup" },
  { value: "inventory", label: "inventory" },
];

function RulesPage() {
  const v = useVault();
  const rules = (v.snapshot?.rules ?? []).filter((r) => r.rule !== null).sort((a, b) => a.hlc.wall_ms - b.hlc.wall_ms);
  const [editing, setEditing] = useState<{ id: string | null; rule: Rule } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addDefaults = async () => {
    const vault = vaultStore.vault;
    if (!vault) return;
    try {
      await vaultStore.commit(defaultRules().map((r) => vault.ruleUpsertOp(null, r)));
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <>
      <PageHeader
        title="Rules"
        description="Which files each device tracks, and how. Rules live in the vault, so every device applies the same set. The first matching rule wins."
        actions={
          <>
            {rules.length === 0 ? (
              <Button variant="outline" onClick={() => void addDefaults()}>
                Add default rules
              </Button>
            ) : null}
            <Button
              onClick={() =>
                setEditing({
                  id: null,
                  rule: {
                    pattern: "**/.env",
                    mode: "sync",
                    root: "~/projects",
                    exclude: [],
                    device_id: null,
                    enabled: true,
                  },
                })
              }
            >
              <PlusIcon /> New rule
            </Button>
          </>
        }
      />
      <ErrorText error={error} />
      {rules.length === 0 ? (
        <EmptyState title="No rules in the vault">
          The CLI ships with sensible defaults (env files under ~/projects, dotfiles as backups). Add them here or let
          the first daemon do it.
        </EmptyState>
      ) : (
        <ul className="divide-y rounded-xl border bg-card" data-testid="rules-list">
          {rules.map((r) => {
            const rule = r.rule as Rule;
            return (
              <li key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-mono text-sm">
                    <span className="text-muted-foreground">{rule.root}/</span>
                    {rule.pattern}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {rule.exclude.length > 0 ? `excludes ${rule.exclude.join(", ")}` : "no excludes"}
                    {rule.device_id ? ` · only device ${rule.device_id.slice(0, 8)}` : ""}
                    {!rule.enabled ? " · disabled" : ""}
                  </p>
                </div>
                <Badge variant={rule.mode === "sync" ? "default" : rule.mode === "backup" ? "secondary" : "outline"}>
                  {rule.mode}
                </Badge>
                <IconButton label="Edit rule" onClick={() => setEditing({ id: r.id, rule })}>
                  <PencilIcon />
                </IconButton>
                <Confirm
                  trigger={
                    <IconButton label="Delete rule">
                      <Trash2Icon />
                    </IconButton>
                  }
                  title="Delete this rule?"
                  description="Files it matched stop being tracked on the next scan; existing history is kept."
                  actionLabel="Delete"
                  destructive
                  onConfirm={async () => {
                    const op = vaultStore.vault?.ruleDeleteOp(r.id);
                    if (op) await vaultStore.commit([op]);
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
      <RuleDialog editing={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function RuleDialog({ editing, onClose }: { editing: { id: string | null; rule: Rule } | null; onClose: () => void }) {
  const [draft, setDraft] = useState<Rule>({
    pattern: "",
    mode: "sync",
    root: "~",
    exclude: [],
    device_id: null,
    enabled: true,
  });
  const [seen, setSeen] = useState<typeof editing>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (editing !== seen) {
    setSeen(editing);
    if (editing) setDraft(editing.rule);
    setError(null);
  }
  const save = async () => {
    if (!draft.pattern.trim() || !draft.root.trim()) {
      setError("Pattern and root are required");
      return;
    }
    setBusy(true);
    try {
      const op = vaultStore.vault?.ruleUpsertOp(editing?.id ?? null, {
        ...draft,
        pattern: draft.pattern.trim(),
        root: draft.root.trim(),
      });
      if (op) await vaultStore.commit([op]);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit rule" : "New rule"}</DialogTitle>
          <DialogDescription>
            Globs are evaluated relative to the root; `~` is the home directory on each device.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="root">Root</Label>
            <Input
              id="root"
              className="font-mono"
              value={draft.root}
              onChange={(e) => setDraft({ ...draft, root: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pattern">Pattern</Label>
            <Input
              id="pattern"
              className="font-mono"
              value={draft.pattern}
              onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="exclude">Excludes (comma separated)</Label>
            <Input
              id="exclude"
              className="font-mono"
              value={draft.exclude.join(", ")}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  exclude: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1.5">
              <Label>Mode</Label>
              <Select value={draft.mode} onValueChange={(m) => setDraft({ ...draft, mode: m ?? "sync" })} items={MODES}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="enabled"
                checked={draft.enabled}
                onCheckedChange={(c) => setDraft({ ...draft, enabled: c })}
              />
              <Label htmlFor="enabled">Enabled</Label>
            </div>
          </div>
          <ErrorText error={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
